import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface Notification {
  id: string
  user_id: string
  title: string
  message: string
  type?: string
  read: boolean
  created_at: string
  event_id?: string
  event_title?: string
  event_date?: string
  event_status?: string
}

// Check if user is in demo mode
function isDemoUser(userId: string | undefined): boolean {
  return !!userId && userId.startsWith('demo-')
}

// Demo notifications for testing
const DEMO_NOTIFICATIONS: Notification[] = [
  {
    id: 'demo-notif-1',
    user_id: 'demo-admin-test-pl',
    title: 'Witaj w trybie demo!',
    message: 'To jest przykładowe powiadomienie w trybie demonstracyjnym.',
    type: 'info',
    read: false,
    created_at: new Date().toISOString(),
  },
]

export function useNotifications() {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const fetchUnreadCount = useCallback(async () => {
    if (!user || !user.id) {
      setUnreadCount(0)
      return
    }

    // Demo mode - return mock count
    if (isDemoUser(user.id)) {
      setUnreadCount(DEMO_NOTIFICATIONS.filter(n => !n.read).length)
      return
    }

    try {
      const { count, error: countError } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false)

      if (countError) {
        console.error('Error fetching unread count:', countError)
      } else {
        setUnreadCount(count || 0)
      }
    } catch (err: any) {
      console.error('Error fetching unread count:', err)
    }
  }, [user])

  const fetchNotifications = useCallback(async () => {
    if (!user || !user.id) {
      setLoading(false)
      return
    }

    // Demo mode - return mock notifications
    if (isDemoUser(user.id)) {
      setNotifications(DEMO_NOTIFICATIONS)
      setLoading(false)
      return
    }

    setIsFetching(true)
    setError(null)

    try {
      // Fetch all notifications (both read and unread)
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50) // Increased limit to show more notifications

      if (fetchError) {
        setError(fetchError.message)
        setLoading(false)
      } else {
        // Fetch event information for notifications that have event_id
        const notificationsWithEvents = await Promise.all(
          (data || []).map(async (notification: any) => {
            if (notification.event_id) {
              try {
                const { data: eventData, error: eventError } = await supabase
                  .from('events')
                  .select('id, title, event_date, status')
                  .eq('id', notification.event_id)
                  .single()

                if (!eventError && eventData) {
                  return {
                    ...notification,
                    event_title: eventData.title,
                    event_date: eventData.event_date,
                    event_status: eventData.status,
                  }
                }
              } catch (err) {
                console.error('Error fetching event for notification:', err)
              }
            }
            return notification
          })
        )

        // Sort: unread first, then by created_at descending
        const sortedNotifications = notificationsWithEvents.sort((a, b) => {
          // First sort by read status (unread first)
          if (a.read !== b.read) {
            return a.read ? 1 : -1
          }
          // Then by created_at (newest first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })

        setNotifications(sortedNotifications)
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [user])

  useEffect(() => {
    fetchNotifications()
    fetchUnreadCount()
  }, [fetchNotifications, fetchUnreadCount])

  // Subscribe to realtime updates (skip for demo users)
  useEffect(() => {
    if (!user || !user.id) return
    if (isDemoUser(user.id)) return // Skip realtime for demo users

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refetch on new notification
          fetchNotifications()
          fetchUnreadCount()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refetch when notification is marked as read
          fetchNotifications()
          fetchUnreadCount()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchNotifications, fetchUnreadCount])

  const markAsRead = useCallback(async (id: string) => {
    // Demo mode - update local state only
    if (user && isDemoUser(user.id)) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
      return
    }

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id)

      if (error) {
        console.error('Error marking notification as read:', error)
        return
      }

      // Refetch after marking as read
      await fetchNotifications()
      await fetchUnreadCount()
    } catch (err: any) {
      console.error('Error marking notification as read:', err)
    }
  }, [user, fetchNotifications, fetchUnreadCount])

  const markAsUnread = useCallback(async (id: string) => {
    // Demo mode - update local state only
    if (user && isDemoUser(user.id)) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: false } : n))
      setUnreadCount(prev => prev + 1)
      return
    }

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: false })
        .eq('id', id)

      if (error) {
        console.error('Error marking notification as unread:', error)
        return
      }

      // Refetch after marking as unread
      await fetchNotifications()
      await fetchUnreadCount()
    } catch (err: any) {
      console.error('Error marking notification as unread:', err)
    }
  }, [user, fetchNotifications, fetchUnreadCount])

  // loading = true tylko gdy faktycznie fetchujemy i nie mamy danych
  // Ale tylko jeśli user jest zalogowany (query powinno się wykonać)
  const isLoading = !!user && !!user.id && isFetching && notifications.length === 0

  return {
    notifications,
    loading: isLoading,
    markAsRead,
    markAsUnread,
    fetchNotifications,
    unreadCount,
  }
}
